import z from 'zod';
import { AllCharLabelSchema, UUIDSchema, UnixTimestampSchema } from '../common/schemas';
import {
  DBJsonObjectSchema,
  DBSha256Schema,
  DBTimestampSchema,
  JsonObjectSchema,
  Sha256Schema,
} from '../common/sql';
import { ProcessingStatus } from './enums';

/**
 * DB Schemas
 */

export const DBSourceDocumentIdSchema = UUIDSchema;
export const DBSourceDocumentOriginalFilenameSchema = z.string().min(1).max(512);
export const DBSourceDocumentDisplayNameSchema = AllCharLabelSchema.min(1).max(1024).nullable();
export const DBSourceDocumentPageCountSchema = z.number().int().positive().nullable();
export const DBSourceDocumentSubjectHintSchema = z.string().min(1).max(64).nullable();
export const DBSourceDocumentInstitutionHintSchema = AllCharLabelSchema.min(1).max(256).nullable();
export const DBSourceDocumentYearSchema = z.number().int().min(1900).max(2200).nullable();
export const DBSourceDocumentPaperTitleSchema = AllCharLabelSchema.min(1).max(512).nullable();
export const DBSourceDocumentPaperReferenceSchema = z.string().min(1).max(256).nullable();
export const DBSourceDocumentRightsNoteSchema = AllCharLabelSchema.min(1).max(2048).nullable();
export const DBSourceDocumentStatusSchema = z.enum(ProcessingStatus);
/** The longest status detail a document or a digitisation run stores. */
export const SOURCE_DOCUMENT_STATUS_DETAIL_MAX_LENGTH = 2048;
export const DBSourceDocumentStatusDetailSchema = AllCharLabelSchema.min(1)
  .max(SOURCE_DOCUMENT_STATUS_DETAIL_MAX_LENGTH)
  .nullable();

export const DBSourceDocumentSchema = z.object({
  id: DBSourceDocumentIdSchema,
  originalFilename: DBSourceDocumentOriginalFilenameSchema,
  displayName: DBSourceDocumentDisplayNameSchema,
  assetId: UUIDSchema,
  sha256: DBSha256Schema,
  pageCount: DBSourceDocumentPageCountSchema,
  documentKindId: UUIDSchema,
  subjectHint: DBSourceDocumentSubjectHintSchema,
  sourceTypeId: UUIDSchema,
  institutionId: UUIDSchema.nullable(),
  institutionHint: DBSourceDocumentInstitutionHintSchema,
  year: DBSourceDocumentYearSchema,
  paperTitle: DBSourceDocumentPaperTitleSchema,
  paperReference: DBSourceDocumentPaperReferenceSchema,
  rightsNote: DBSourceDocumentRightsNoteSchema,
  contentFingerprint: DBSha256Schema.nullable(),
  duplicateOfDocumentId: UUIDSchema.nullable(),
  status: DBSourceDocumentStatusSchema,
  statusDetail: DBSourceDocumentStatusDetailSchema,
  uploadedBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBDocumentPageSchema = z.object({
  id: UUIDSchema,
  sourceDocumentId: UUIDSchema,
  pageNumber: z.number().int().positive(),
  assetId: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
});

export const DBDocumentIndexEntrySchema = z.object({
  id: UUIDSchema,
  sourceDocumentId: UUIDSchema,
  sequenceInDocument: z.number().int().positive(),
  originalQuestionId: z.string().min(1).max(64),
  originalQuestionNumber: z.string().min(1).max(64),
  marks: z.number().int().min(0).nullable(),
  startPage: z.number().int().positive(),
  endPage: z.number().int().positive(),
  createdAt: DBTimestampSchema,
});

export const DBDigitisationRunSchema = z.object({
  id: UUIDSchema,
  sourceDocumentId: UUIDSchema,
  engineName: z.string().min(1).max(128),
  engineVersion: z.string().min(1).max(64),
  config: DBJsonObjectSchema,
  configHash: DBSha256Schema,
  status: DBSourceDocumentStatusSchema,
  statusDetail: DBSourceDocumentStatusDetailSchema,
  stats: DBJsonObjectSchema,
  startedAt: DBTimestampSchema.nullable(),
  finishedAt: DBTimestampSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/**
 * Client Schemas
 */

export const SourceDocumentIdSchema = UUIDSchema;
export const SourceDocumentSha256Schema = Sha256Schema;
export const SourceDocumentOriginalFilenameSchema = DBSourceDocumentOriginalFilenameSchema;
export const SourceDocumentDisplayNameSchema = DBSourceDocumentDisplayNameSchema.openapi({
  description: 'The name the document is listed under. Not unique.',
  example: '2023 Sydney Girls High School Mathematics Extension 2 School assessment (Task 3)',
});
export const SourceDocumentPageCountSchema = DBSourceDocumentPageCountSchema;
export const SourceDocumentSubjectHintSchema = DBSourceDocumentSubjectHintSchema.openapi({
  description: 'The subject code the digitisation engine read from the document',
  example: 'physics',
});
export const SourceDocumentInstitutionHintSchema = DBSourceDocumentInstitutionHintSchema.openapi({
  description: 'The institution name printed in the document, when no institution row matches it',
  example: 'Somewhere Grammar School',
});
export const SourceDocumentYearSchema = DBSourceDocumentYearSchema;
export const SourceDocumentPaperTitleSchema = DBSourceDocumentPaperTitleSchema;
export const SourceDocumentPaperReferenceSchema = DBSourceDocumentPaperReferenceSchema;
export const SourceDocumentRightsNoteSchema = DBSourceDocumentRightsNoteSchema;
export const SourceDocumentStatusSchema = z.enum(ProcessingStatus).openapi({
  description: 'Processing status of the document',
  example: ProcessingStatus.Processed,
});
export const SourceDocumentStatusDetailSchema = DBSourceDocumentStatusDetailSchema;

export const SourceDocumentSchema = z.object({
  id: SourceDocumentIdSchema,
  originalFilename: SourceDocumentOriginalFilenameSchema,
  displayName: SourceDocumentDisplayNameSchema,
  assetId: UUIDSchema,
  sha256: Sha256Schema,
  pageCount: SourceDocumentPageCountSchema,
  documentKindId: UUIDSchema,
  subjectHint: SourceDocumentSubjectHintSchema,
  sourceTypeId: UUIDSchema,
  institutionId: UUIDSchema.nullable(),
  institutionHint: SourceDocumentInstitutionHintSchema,
  year: SourceDocumentYearSchema,
  paperTitle: SourceDocumentPaperTitleSchema,
  paperReference: SourceDocumentPaperReferenceSchema,
  rightsNote: SourceDocumentRightsNoteSchema,
  contentFingerprint: Sha256Schema.nullable(),
  duplicateOfDocumentId: UUIDSchema.nullable(),
  status: SourceDocumentStatusSchema,
  statusDetail: SourceDocumentStatusDetailSchema,
  uploadedBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const DocumentPageSchema = z.object({
  id: UUIDSchema,
  sourceDocumentId: UUIDSchema,
  pageNumber: z.number().int().positive(),
  assetId: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
});

export const DocumentIndexEntrySchema = z.object({
  id: UUIDSchema,
  sourceDocumentId: UUIDSchema,
  sequenceInDocument: z.number().int().positive(),
  originalQuestionId: z.string().min(1).max(64),
  originalQuestionNumber: z.string().min(1).max(64),
  marks: z.number().int().min(0).nullable(),
  startPage: z.number().int().positive(),
  endPage: z.number().int().positive(),
  createdAt: UnixTimestampSchema,
});

export const DigitisationRunSchema = z.object({
  id: UUIDSchema,
  sourceDocumentId: UUIDSchema,
  engineName: z.string().min(1).max(128),
  engineVersion: z.string().min(1).max(64),
  config: JsonObjectSchema,
  configHash: Sha256Schema,
  status: SourceDocumentStatusSchema,
  statusDetail: SourceDocumentStatusDetailSchema,
  stats: JsonObjectSchema,
  startedAt: UnixTimestampSchema.nullable(),
  finishedAt: UnixTimestampSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});
